import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface DeadlineItem {
  id: string;
  title: string;
  dueDate: Date;
  type: 'task' | 'project';
  assignedUsers: string[];
  organizationId: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

// Firebase function hívás
const createNotificationFunction = httpsCallable(functions, 'createNotification');

export class DeadlineReminderService {
  // Ellenőrzi a közelgő határidőket és küld értesítéseket
  static async checkUpcomingDeadlines(): Promise<void> {
    try {
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      // Feladatok ellenőrzése
      await this.checkTaskDeadlines(now, tomorrow, nextWeek);
      
      // Projektek ellenőrzése
      await this.checkProjectDeadlines(now, tomorrow, nextWeek);
      
      console.log('Deadline check completed successfully');
    } catch (error) {
      console.error('Error checking deadlines:', error);
    }
  }

  // Feladat határidők ellenőrzése
  private static async checkTaskDeadlines(now: Date, tomorrow: Date, nextWeek: Date): Promise<void> {
    const tasksQuery = query(
      collection(db, 'tasks'),
      where('dueDate', '>=', Timestamp.fromDate(now)),
      where('dueDate', '<=', Timestamp.fromDate(nextWeek)),
      where('status', '!=', 'completed')
    );

    const tasksSnapshot = await getDocs(tasksQuery);
    
    for (const taskDoc of tasksSnapshot.docs) {
      const task = taskDoc.data();
      const dueDate = task.dueDate.toDate();
      
      // Ellenőrizzük, hogy holnap jár-e le
      if (dueDate <= tomorrow && dueDate > now) {
        await this.sendDeadlineReminder({
          id: taskDoc.id,
          title: task.title,
          dueDate: dueDate,
          type: 'task',
          assignedUsers: task.assignedTo ? [task.assignedTo] : [],
          organizationId: task.organizationId,
          priority: task.priority || 'medium'
        }, 'tomorrow');
      }
      // Vagy lejárt-e
      else if (dueDate < now) {
        await this.sendOverdueNotification({
          id: taskDoc.id,
          title: task.title,
          dueDate: dueDate,
          type: 'task',
          assignedUsers: task.assignedTo ? [task.assignedTo] : [],
          organizationId: task.organizationId,
          priority: task.priority || 'medium'
        });
      }
    }
  }

  // Projekt határidők ellenőrzése
  private static async checkProjectDeadlines(now: Date, tomorrow: Date, nextWeek: Date): Promise<void> {
    const projectsQuery = query(
      collection(db, 'projects'),
      where('endDate', '>=', Timestamp.fromDate(now)),
      where('endDate', '<=', Timestamp.fromDate(nextWeek)),
      where('status', 'in', ['active', 'planning'])
    );

    const projectsSnapshot = await getDocs(projectsQuery);
    
    for (const projectDoc of projectsSnapshot.docs) {
      const project = projectDoc.data();
      const endDate = project.endDate.toDate();
      
      // Ellenőrizzük, hogy holnap jár-e le
      if (endDate <= tomorrow && endDate > now) {
        await this.sendDeadlineReminder({
          id: projectDoc.id,
          title: project.name,
          dueDate: endDate,
          type: 'project',
          assignedUsers: project.assignedUsers || [],
          organizationId: project.organizationId,
          priority: project.priority || 'medium'
        }, 'tomorrow');
      }
      // Vagy lejárt-e
      else if (endDate < now) {
        await this.sendOverdueNotification({
          id: projectDoc.id,
          title: project.name,
          dueDate: endDate,
          type: 'project',
          assignedUsers: project.assignedUsers || [],
          organizationId: project.organizationId,
          priority: project.priority || 'medium'
        });
      }
    }
  }

  // Deadline reminder értesítés küldése
  private static async sendDeadlineReminder(item: DeadlineItem, timeframe: 'tomorrow' | 'week'): Promise<void> {
    const title = timeframe === 'tomorrow' 
      ? `Határidő holnap jár le`
      : `Határidő egy héten belül jár le`;
      
    const message = `${item.type === 'task' ? 'Feladat' : 'Projekt'}: ${item.title}`;

    for (const userId of item.assignedUsers) {
      try {
        await createNotificationFunction({
          type: 'deadline_reminder',
          title: title,
          message: message,
          data: {
            itemId: item.id,
            itemType: item.type,
            dueDate: item.dueDate.toISOString()
          },
          priority: timeframe === 'tomorrow' ? 'high' : 'medium',
          userId: userId,
          organizationId: item.organizationId,
          createdBy: 'system',
          createdByName: 'Rendszer',
          actionUrl: item.type === 'task' ? `/tasks?task=${item.id}` : `/projects?project=${item.id}`
        });
      } catch (error) {
        console.error(`Error sending deadline reminder to user ${userId}:`, error);
      }
    }
  }

  // Lejárt határidő értesítés küldése
  private static async sendOverdueNotification(item: DeadlineItem): Promise<void> {
    const title = `Határidő lejárt`;
    const message = `${item.type === 'task' ? 'Feladat' : 'Projekt'}: ${item.title}`;
    const daysOverdue = Math.floor((new Date().getTime() - item.dueDate.getTime()) / (1000 * 60 * 60 * 24));

    for (const userId of item.assignedUsers) {
      try {
        await createNotificationFunction({
          type: item.type === 'task' ? 'task_overdue' : 'deadline_reminder',
          title: title,
          message: `${message} (${daysOverdue} napja lejárt)`,
          data: {
            itemId: item.id,
            itemType: item.type,
            dueDate: item.dueDate.toISOString(),
            daysOverdue: daysOverdue
          },
          priority: 'urgent',
          userId: userId,
          organizationId: item.organizationId,
          createdBy: 'system',
          createdByName: 'Rendszer',
          actionUrl: item.type === 'task' ? `/tasks?task=${item.id}` : `/projects?project=${item.id}`
        });
      } catch (error) {
        console.error(`Error sending overdue notification to user ${userId}:`, error);
      }
    }
  }

  // Manuális deadline ellenőrzés (fejlesztés/teszt célokra)
  static async runManualCheck(): Promise<string> {
    try {
      await this.checkUpcomingDeadlines();
      return 'Deadline check completed successfully';
    } catch (error) {
      console.error('Manual deadline check failed:', error);
      return `Deadline check failed: ${error}`;
    }
  }
}

// Hook a deadline service használatához
export const useDeadlineReminder = () => {
  const runDeadlineCheck = async (): Promise<string> => {
    return await DeadlineReminderService.runManualCheck();
  };

  return {
    runDeadlineCheck
  };
}; 